/** Local browsing/purchase profile for upsell personalization (localStorage). */

export const USER_PROFILE_STORAGE_KEY = 'botlecap_user_profile';
export const MAX_PROFILE_EVENTS = 50;

const TYPE_WEIGHT = {
  purchase: 4,
  add: 3,
  click: 2,
  view: 1,
};

export function getEffectiveProductPrice(product) {
  if (!product) return 0;
  const storeKey = product.selectedStore != null ? String(product.selectedStore).padStart(3, '0') : '001';
  return product.store_data?.[`store_${storeKey}`]?.price ?? product.price ?? 0;
}

export function extractProductAttrs(product) {
  if (!product) {
    return {
      pays_origine: '',
      region_origine: '',
      cepage: '',
      color: '',
      categorias_flat: '',
      categorias_root: '',
      rating: null,
    };
  }
  const pays = product.attributes?.pays_origine || '';
  const region = product.attributes?.region_origine || '';
  const cepageRaw = product.attributes?.cepage;
  const cepage = Array.isArray(cepageRaw) ? cepageRaw.join(', ') : String(cepageRaw || '');
  const color = product.color || '';
  const cf = product.categorias_flat;
  const categorias_flat = Array.isArray(cf) ? cf[0] || '' : String(cf || '');
  const categorias_root = getCategoryRoot(categorias_flat);
  const rating =
    product.customer_rating != null
      ? Number(product.customer_rating)
      : product.attributes?.reviews_average_rating != null
        ? Number(product.attributes.reviews_average_rating)
        : null;
  return {
    pays_origine: pays,
    region_origine: region,
    cepage,
    color,
    categorias_flat,
    categorias_root,
    rating: Number.isFinite(rating) ? rating : null,
  };
}

export function getCategoryRoot(categorias_flat) {
  if (categorias_flat == null || categorias_flat === '') return '';
  const s = Array.isArray(categorias_flat) ? String(categorias_flat[0] || '') : String(categorias_flat);
  return s.split(' > ')[0].split(' / ')[0].trim();
}

function loadRaw() {
  try {
    const raw = localStorage.getItem(USER_PROFILE_STORAGE_KEY);
    if (!raw) return { events: [] };
    const data = JSON.parse(raw);
    return Array.isArray(data.events) ? data : { events: [] };
  } catch {
    return { events: [] };
  }
}

function saveRaw(data) {
  try {
    localStorage.setItem(USER_PROFILE_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * @param {{ type: 'view' | 'click' | 'add' | 'purchase', product: object }} param0
 */
export function recordEvent({ type, product }) {
  if (!product?.objectID) return;
  const attrs = extractProductAttrs(product);
  const price = getEffectiveProductPrice(product);
  const event = {
    t: Date.now(),
    type,
    objectID: String(product.objectID),
    price,
    attrs,
  };
  const data = loadRaw();
  data.events.push(event);
  if (data.events.length > MAX_PROFILE_EVENTS) {
    data.events = data.events.slice(-MAX_PROFILE_EVENTS);
  }
  saveRaw(data);
}

function percentile(sortedArr, p) {
  if (!sortedArr.length) return null;
  const idx = Math.min(sortedArr.length - 1, Math.max(0, Math.floor((p / 100) * (sortedArr.length - 1))));
  return sortedArr[idx];
}

function rankByWeightedCount(map) {
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => key)
    .filter(Boolean);
}

/**
 * @returns {{
 *   topCountries: string[],
 *   topRegions: string[],
 *   topGrapes: string[],
 *   topColors: string[],
 *   topCategories: string[],
 *   priceBand: { p25: number|null, p50: number|null, p75: number|null },
 *   avgRatingInterest: number|null,
 * }}
 */
export function getAffinities() {
  const { events } = loadRaw();
  const now = Date.now();
  const dayMs = 86400000;

  const countryW = {};
  const regionW = {};
  const grapeW = {};
  const colorW = {};
  const catW = {};
  const prices = [];
  let ratingSum = 0;
  let ratingCount = 0;

  for (const ev of events) {
    const tw = TYPE_WEIGHT[ev.type] ?? 1;
    const ageDays = (now - ev.t) / dayMs;
    const decay = Math.exp(-ageDays / 30);
    const w = tw * decay;
    const a = ev.attrs || {};
    if (a.pays_origine) countryW[a.pays_origine] = (countryW[a.pays_origine] || 0) + w;
    if (a.region_origine) regionW[a.region_origine] = (regionW[a.region_origine] || 0) + w;
    if (a.cepage) {
      for (const g of tokenizeGrapes(a.cepage)) {
        grapeW[g] = (grapeW[g] || 0) + w;
      }
    }
    if (a.color) colorW[a.color] = (colorW[a.color] || 0) + w;
    if (a.categorias_root) catW[a.categorias_root] = (catW[a.categorias_root] || 0) + w;
    if (ev.price > 0) prices.push(ev.price);
    if (a.rating != null && Number.isFinite(a.rating)) {
      ratingSum += a.rating * w;
      ratingCount += w;
    }
  }

  prices.sort((x, y) => x - y);
  const priceBand = {
    p25: prices.length ? percentile(prices, 25) : null,
    p50: prices.length ? percentile(prices, 50) : null,
    p75: prices.length ? percentile(prices, 75) : null,
  };

  return {
    topCountries: rankByWeightedCount(countryW).slice(0, 5),
    topRegions: rankByWeightedCount(regionW).slice(0, 5),
    topGrapes: rankByWeightedCount(grapeW).slice(0, 8),
    topColors: rankByWeightedCount(colorW).slice(0, 3),
    topCategories: rankByWeightedCount(catW).slice(0, 3),
    priceBand,
    avgRatingInterest: ratingCount > 0 ? ratingSum / ratingCount : null,
  };
}

export function tokenizeGrapes(cepageStr) {
  if (!cepageStr) return [];
  return String(cepageStr)
    .split(/[,;/]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function normalizeGrapeKey(grape) {
  return String(grape || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

export function grapesMatch(a, b) {
  const x = normalizeGrapeKey(a);
  const y = normalizeGrapeKey(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

/**
 * Parse cepage into grapes ordered by prominence (first listed or explicit %).
 * @returns {{ display: string, normalized: string, weight: number, explicitPercent: boolean }[]}
 */
export function parseGrapeEntries(cepageStr) {
  if (!cepageStr) return [];
  const parts = String(cepageStr)
    .split(/[,;/]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const entries = parts.map((part, i) => {
    let display = part;
    let weight = 100 - i * 15;
    let explicitPercent = false;

    const pctSuffix = part.match(/^(.+?)\s+(\d+(?:\.\d+)?)\s*%$/);
    const pctPrefix = part.match(/^(\d+(?:\.\d+)?)\s*%?\s+(.+)$/);
    const pctParen = part.match(/^(.+?)\s*\((\d+(?:\.\d+)?)\s*%?\)$/);

    if (pctSuffix) {
      display = pctSuffix[1].trim();
      weight = parseFloat(pctSuffix[2]);
      explicitPercent = true;
    } else if (pctPrefix) {
      display = pctPrefix[2].trim();
      weight = parseFloat(pctPrefix[1]);
      explicitPercent = true;
    } else if (pctParen) {
      display = pctParen[1].trim();
      weight = parseFloat(pctParen[2]);
      explicitPercent = true;
    }

    return {
      display,
      normalized: normalizeGrapeKey(display),
      weight,
      explicitPercent,
    };
  });

  return entries.sort((a, b) => b.weight - a.weight);
}

function isLeadGrape(entry, sortedEntries) {
  const rank = sortedEntries.findIndex(
    (e) => e.normalized === entry.normalized && e.display === entry.display
  );
  if (rank >= 0 && rank <= 1) return true;
  return entry.explicitPercent && entry.weight >= 30;
}

/**
 * Pick a grape label for upsell copy — only lead grapes (top 2 in blend, or ≥30% if stated).
 * Prefers overlap with the cart item, then user affinity; never surfaces minor blending grapes.
 */
export function pickGrapeReason({ origCepage, candCepage, topGrapes = [] }) {
  const candEntries = parseGrapeEntries(candCepage);
  if (!candEntries.length) return null;

  const origKeys = tokenizeGrapes(origCepage).map(normalizeGrapeKey);
  const tops = (topGrapes || []).map(normalizeGrapeKey);

  for (const entry of candEntries) {
    if (!isLeadGrape(entry, candEntries)) continue;
    for (const o of origKeys) {
      if (grapesMatch(entry.normalized, o)) return entry.display;
    }
  }

  for (const entry of candEntries) {
    if (!isLeadGrape(entry, candEntries)) continue;
    for (const t of tops.slice(0, 5)) {
      if (grapesMatch(entry.normalized, t)) return entry.display;
    }
  }

  return null;
}

export function grapeMatchesAffinity(cepageStr, topGrapes) {
  return pickGrapeReason({ origCepage: '', candCepage: cepageStr, topGrapes });
}
